import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { WorkbookPagesTab } from '@/components/content-generator/editor/tabs/WorkbookPagesTab';
import QuickCreatePageModal from '@/components/content-generator/editor/pages/components/QuickCreatePageModal';
import NewPageModal from '@/components/content-generator/editor/pages/components/NewPageModal';
import SimplePageEditorModal from '@/components/private-text-generator/SimplePageEditorModal';
import { ProjectOutletContext } from '@/types/project';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/errors/use-toast';
import { useQuestDetection } from '@/hooks/onboarding/useQuestDetection';

// Empty state imports
import { EmptyStateLayout } from '@/components/shared/empty-states/EmptyStateLayout';
import { ProjectImportPagesEmptyState } from '@/components/shared/import';
import { FileText } from 'lucide-react';

/**
 * ProjectPagesPage - Pages list route for project system
 * Contains workbook pages management functionality
 */
const ProjectPagesPage: React.FC = () => {
  const { project, goal, agentThreadId, navigationData, registerPageCreationHandler } = useOutletContext<ProjectOutletContext>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Initialize quest detection for this page
  useQuestDetection();
  
  // Modal state for page creation
  const [isNewPageModalOpen, setNewPageModalOpen] = useState(false);
  const [isAdvancedPageModalOpen, setAdvancedPageModalOpen] = useState(false);
  const [advancedPageInitialTitle, setAdvancedPageInitialTitle] = useState<string>('');
  
  // Modal state for page editing (following private-text-generator pattern)
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  
  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">
          Project not found.
        </p>
      </div>
    );
  }
  
  const pages = navigationData?.pages || [];
  
  // Empty state detection logic
  const shouldShowCenteredLayout = pages.length === 0;
  
  // URL parameter handling: detect external page routes and auto-open modal
  useEffect(() => {
    // Check for external openPage parameter (for backwards compatibility)
    const searchParams = new URLSearchParams(location.search);
    const openPageId = searchParams.get('openPage');
    
    if (openPageId && openPageId !== editingPageId) {
      console.log('[ProjectPagesPage] Auto-opening modal from URL parameter:', openPageId);
      setEditingPageId(openPageId);
      
      // Clean up the URL parameter after opening modal
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('openPage');
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [location.search, editingPageId, navigate]);
  
  const handleCreatePage = () => {
    setNewPageModalOpen(true);
  };

  // Handle Generate Content button click from QuickCreatePageModal
  const handleGenerateWithAI = (title: string) => {
    console.log('[ProjectPagesPage] Opening Advanced AI Page Creation modal with title:', title);
    setAdvancedPageInitialTitle(title);
    setNewPageModalOpen(false); // Close quick create modal
    setAdvancedPageModalOpen(true); // Open advanced modal
  };
  
  // Register page creation handler with the layout
  useEffect(() => {
    if (registerPageCreationHandler) {
      registerPageCreationHandler(handleCreatePage);
    }
    
    // Cleanup: unregister when component unmounts
    return () => {
      if (registerPageCreationHandler) {
        registerPageCreationHandler(undefined);
      }
    };
  }, [registerPageCreationHandler]);
  
  const handleEditPage = (pageId: string) => {
    console.log('[ProjectPagesPage] Opening page in modal (no URL change):', pageId);
    // Simple direct modal opening - no URL navigation
    setEditingPageId(pageId);
  };
  
  // Handle closing the page editor modal
  const handleCloseModal = () => {
    console.log('[ProjectPagesPage] Closing page editor modal');
    setEditingPageId(null);
    // No navigation needed - just close modal
  };
  
  // Handle page deletion from modal
  const handlePageDeleted = (pageId: string) => {
    console.log('[ProjectPagesPage] Page deleted:', pageId);
    setEditingPageId(null);
    
    // No navigation needed - just close modal
    
    // Invalidate project navigation query to refresh page list
    queryClient.invalidateQueries({ queryKey: ['project-navigation', project.id] });
    
    toast({
      title: "Page Deleted",
      description: "The page has been deleted successfully.",
    });
  };
  
  const handlePageCreated = (pageId: string) => {
    // Open the newly created page in modal (no navigation)
    setEditingPageId(pageId);
  };

  const handleEmptyStatePageCreation = () => {
    // Trigger the page creation modal from empty state
    handleCreatePage();
  };

  const handleDirectPageCreated = (pageId: string) => {
    // Open the newly created page in modal (no navigation)
    setEditingPageId(pageId);
  };
  
  return (
    <>
      {shouldShowCenteredLayout ? (
        /* Empty State Layout - Import Documents System */
        <EmptyStateLayout
          icon={<FileText className="h-8 w-8 text-purple-500 dark:text-purple-400" />}
          title="Import Documents"
          description="Organize your content documents in this project and collaborate with your team"
          variant="purple"
        >
          <ProjectImportPagesEmptyState 
            projectId={project.id}
            onImportComplete={() => {
              // The page will automatically refresh when items are imported
              console.log('Pages imported successfully');
            }}
          />
        </EmptyStateLayout>
      ) : (
        /* Normal Layout - Show WorkbookPagesTab */
        <WorkbookPagesTab 
          pageId={project.id}
          onCreatePage={handleCreatePage}
          onEditPage={handleEditPage}
        />
      )}
      
      {/* Quick Create Page Modal - Universal Thread Creation */}
      <QuickCreatePageModal
        isOpen={isNewPageModalOpen}
        onClose={() => setNewPageModalOpen(false)}
        workbookId={project.id} // ✅ UNIVERSAL: Project pages just need workbook ID
        onPageCreated={handlePageCreated}
        onGenerateWithAI={handleGenerateWithAI} // ✅ Connect Generate Content button
      />

      {/* Advanced AI Page Creation Modal */}
      <NewPageModal
        isOpen={isAdvancedPageModalOpen}
        onClose={() => {
          setAdvancedPageModalOpen(false);
          setAdvancedPageInitialTitle('');
        }}
        workbookId={project.id}
        initialTitle={advancedPageInitialTitle}
        onPageCreated={handlePageCreated}
      />
      
      {/* Page Editor Modal - Following private-text-generator pattern */}
      <SimplePageEditorModal
        pageId={editingPageId}
        isOpen={!!editingPageId}
        onClose={handleCloseModal}
        onPageDeleted={handlePageDeleted}
        workbookId={project.id} // Provide project ID as workbook context
      />
    </>
  );
};

export default ProjectPagesPage; 